// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HoverTriggerComponent } from '@lfx/shared/components/hover-trigger/hover-trigger.component';
import { InfluenceTooltipEntity } from '@lfx/key-projects/entities/influence-tooltip.entity';
import { ScoreLevel } from '@lfx/key-projects/entities/base-metric.entity';

@Component({
  selector: 'lfx-influence-column',
  imports: [CommonModule, HoverTriggerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <lfx-hover-trigger
      theme="dark"
      [openDelay]="200"
      [closeDelay]="100"
      [cursor]="href() ? 'pointer' : 'help'"
    >
      @if (href()) {
        <a [href]="href()" target="_blank" rel="noopener noreferrer" class="influence-container influence-link">
          <ng-container *ngTemplateOutlet="influenceContent"></ng-container>
        </a>
      } @else {
        <div class="influence-container">
          <ng-container *ngTemplateOutlet="influenceContent"></ng-container>
        </div>
      }
      <div slot="popover-content" class="header-tooltip-content">
        {{ tooltipText() }}
      </div>
    </lfx-hover-trigger>

    <ng-template #influenceContent>
      <div class="influence-icon" [attr.aria-label]="variant()">
        @switch (variant()) {
          @case ('green') {
            <!-- All 4 bars active (Green) -->
            <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.5 0C17.3125 0 18 0.6875 18 1.5V14.5C18 15.3438 17.3125 16 16.5 16C15.6562 16 15 15.3438 15 14.5V1.5C15 0.6875 15.6562 0 16.5 0ZM10 5.5C10 4.6875 10.6562 4 11.5 4C12.3125 4 13 4.6875 13 5.5V14.5C13 15.3438 12.3125 16 11.5 16C10.6562 16 10 15.3438 10 14.5V5.5ZM6.5 8C7.3125 8 8 8.6875 8 9.5V14.5C8 15.3438 7.3125 16 6.5 16C5.65625 16 5 15.3438 5 14.5V9.5C5 8.6875 5.65625 8 6.5 8ZM1.5 12C2.3125 12 3 12.6875 3 13.5V14.5C3 15.3438 2.3125 16 1.5 16C0.65625 16 0 15.3438 0 14.5V13.5C0 12.6875 0.65625 12 1.5 12Z" fill="#10BC8A"/>
            </svg>
          }
          @case ('blue') {
            <!-- 3 bars active (Blue) -->
            <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.5 4C12.3125 4 13 4.6875 13 5.5V14.5C13 15.3438 12.3125 16 11.5 16C10.6562 16 10 15.3438 10 14.5V5.5C10 4.6875 10.6562 4 11.5 4ZM5 9.5C5 8.6875 5.65625 8 6.5 8C7.3125 8 8 8.6875 8 9.5V14.5C8 15.3438 7.3125 16 6.5 16C5.65625 16 5 15.3438 5 14.5V9.5ZM0 13.5C0 12.6875 0.65625 12 1.5 12C2.3125 12 3 12.6875 3 13.5V14.5C3 15.3438 2.3125 16 1.5 16C0.65625 16 0 15.3438 0 14.5V13.5Z" fill="#009AFF"/>
              <path opacity="0.4" d="M15 1.5C15 0.6875 15.6562 0 16.5 0C17.3125 0 18 0.6875 18 1.5V14.5C18 15.3438 17.3125 16 16.5 16C15.6562 16 15 15.3438 15 14.5V1.5Z" fill="#009AFF"/>
            </svg>
          }
          @case ('yellow') {
            <!--  2 bars active (Orange) -->
            <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.5 8C7.3125 8 8 8.6875 8 9.5V14.5C8 15.3438 7.3125 16 6.5 16C5.65625 16 5 15.3438 5 14.5V9.5C5 8.6875 5.65625 8 6.5 8ZM1.5 12C2.3125 12 3 12.6875 3 13.5V14.5C3 15.3438 2.3125 16 1.5 16C0.65625 16 0 15.3438 0 14.5V13.5C0 12.6875 0.65625 12 1.5 12Z" fill="#F89E22"/>
              <path opacity="0.4" d="M16.5 0C17.3125 0 18 0.6875 18 1.5V14.5C18 15.3438 17.3125 16 16.5 16C15.6562 16 15 15.3438 15 14.5V1.5C15 0.6875 15.6562 0 16.5 0ZM11.5 4C12.3125 4 13 4.6875 13 5.5V14.5C13 15.3438 12.3125 16 11.5 16C10.6562 16 10 15.3438 10 14.5V5.5C10 4.6875 10.6562 4 11.5 4Z" fill="#F89E22"/>
            </svg>
          }
          @case ('red') {
            <!-- 1 bar active (Red) -->
            <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 13.5C0 12.6875 0.65625 12 1.5 12C2.3125 12 3 12.6875 3 13.5V14.5C3 15.3438 2.3125 16 1.5 16C0.65625 16 0 15.3438 0 14.5V13.5Z" fill="#EF4444"/>
              <path opacity="0.4" d="M16.5 0C17.3125 0 18 0.6875 18 1.5V14.5C18 15.3438 17.3125 16 16.5 16C15.6562 16 15 15.3438 15 14.5V1.5C15 0.6875 15.6562 0 16.5 0ZM10 5.5C10 4.6875 10.6562 4 11.5 4C12.3125 4 13 4.6875 13 5.5V14.5C13 15.3438 12.3125 16 11.5 16C10.6562 16 10 15.3438 10 14.5V5.5ZM5 9.5C5 8.6875 5.65625 8 6.5 8C7.3125 8 8 8.6875 8 9.5V14.5C8 15.3438 7.3125 16 6.5 16C5.65625 16 5 15.3438 5 14.5V9.5Z" fill="#EF4444"/>
            </svg>
          }
          @default {
            <!-- No bars active (Default) -->
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="18" viewBox="0 0 22 18" fill="none">
              <path d="M2.1875 1.1875L12 8.84375V6.5C12 5.6875 12.6562 5 13.5 5C14.3125 5 15 5.6875 15 6.5V11.1875L17 12.75V2.5C17 1.6875 17.6562 1 18.5 1C19.3125 1 20 1.6875 20 2.5V15.125L20.6875 15.6875C21.0312 15.9375 21.0938 16.4062 20.8125 16.7188C20.5625 17.0625 20.0938 17.125 19.7812 16.8438L1.28125 2.34375C0.9375 2.09375 0.875 1.625 1.15625 1.3125C1.40625 0.96875 1.875 0.90625 2.1875 1.1875ZM18.5 17H18.4688H18.5ZM15 15.5C15 16.3438 14.3125 17 13.5 17C12.6562 17 12 16.3438 12 15.5V12.6875L15 15.0312V15.5ZM10 11.0938V15.5C10 16.3438 9.3125 17 8.5 17C7.65625 17 7 16.3438 7 15.5V10.5C7 10 7.25 9.53125 7.65625 9.28125L10 11.0938ZM3.5 13C4.3125 13 5 13.6875 5 14.5V15.5C5 16.3438 4.3125 17 3.5 17C2.65625 17 2 16.3438 2 15.5V14.5C2 13.6875 2.65625 13 3.5 13Z" fill="#DBDCDE"/>
              <path opacity="0.4" d="M20 15.125L17 12.75V2.5C17 1.6875 17.6562 1 18.5 1C19.3125 1 20 1.6875 20 2.5V15.125ZM15 11.1875L12 8.84375V6.5C12 5.6875 12.6562 5 13.5 5C14.3125 5 15 5.6875 15 6.5V11.1875ZM18.5 17H18.4688H18.5ZM15 15.5C15 16.3438 14.3125 17 13.5 17C12.6562 17 12 16.3438 12 15.5V12.6875L15 15.0312V15.5ZM10 11.0938V15.5C10 16.3438 9.3125 17 8.5 17C7.65625 17 7 16.3438 7 15.5V10.5C7 10 7.25 9.53125 7.65625 9.28125L10 11.0938ZM3.5 13C4.3125 13 5 13.6875 5 14.5V15.5C5 16.3438 4.3125 17 3.5 17C2.65625 17 2 16.3438 2 15.5V14.5C2 13.6875 2.65625 13 3.5 13Z" fill="#DBDCDE"/>
            </svg>
          }
        }
      </div>
      <span class="influence-label lf-body-base">{{ displayText() }}</span>
    </ng-template>
  `,
  styleUrls: ['./influence-column.component.scss']
})
export class InfluenceColumnComponent {
  readonly variant = input<'red' | 'yellow' | 'blue' | 'green' | 'default'>('default');
  readonly displayText = input.required<string>();
  /** Optional URL to navigate to on click (opens in new tab) */
  readonly href = input<string>('');

  protected readonly tooltipText = computed(() => {
    const level = this.displayText();
    if (InfluenceTooltipEntity.isValidLevel(level)) {
      return InfluenceTooltipEntity.getLevelTooltip(level as ScoreLevel);
    }
    return '';
  });
}
